import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { getFamilyMemberFullName } from '@/features/families/mappers';
import type {
  CascadeContactStatus,
  FamilyMemberViewModel,
} from '@/models/family';
import type { UpdateFamilyMemberBody } from '@/features/families/api';

const CONTACT_STATUSES: readonly CascadeContactStatus[] = [
  'NOT_CONTACTED',
  'CONTACTED',
  'TESTED',
  'DECLINED',
];

const SEX_CODES = ['M', 'F', 'O', 'U'] as const;
type SexCode = (typeof SEX_CODES)[number];

interface MemberEditDialogProps {
  readonly member: FamilyMemberViewModel;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (body: UpdateFamilyMemberBody) => void;
}

type Writable<T> = { -readonly [K in keyof T]: T[K] };

const isSexCode = (value: string | null): value is SexCode =>
  value !== null && (SEX_CODES as readonly string[]).includes(value);

/**
 * Edit-member dialog. Wires the broadened `PUT /families/{guid}/members/{id}`:
 *
 *   - contact_status / notes are stored on the family-member row
 *   - sex / deceased / date_of_birth are written through to the linked
 *     person's demographics
 *
 * Submits only the keys that actually changed so the BE's
 * presence-sensitive update leaves untouched fields alone.
 */
export function MemberEditDialog({
  member,
  busy,
  onCancel,
  onSubmit,
}: MemberEditDialogProps): ReactElement {
  const { t } = useTranslation();
  const initialSex = isSexCode(member.sex) ? member.sex : '';
  const [contactStatus, setContactStatus] = useState<CascadeContactStatus>(
    member.contactStatus,
  );
  const [sex, setSex] = useState<SexCode | ''>(initialSex);
  const [dateOfBirth, setDateOfBirth] = useState(member.dateOfBirth ?? '');
  const [deceased, setDeceased] = useState<boolean>(member.deceased === true);
  const [notes, setNotes] = useState(member.notes ?? '');

  const name = getFamilyMemberFullName(member) ?? t('patient.demographics.emptyValue');

  const body = useMemo<UpdateFamilyMemberBody>(() => {
    const trimmedDob = dateOfBirth.trim();
    const trimmedNotes = notes;
    const next: Writable<UpdateFamilyMemberBody> = {};
    if (contactStatus !== member.contactStatus) {
      next.contact_status = contactStatus;
    }
    if (sex !== initialSex) {
      next.sex = sex === '' ? null : sex;
    }
    const initialDob = member.dateOfBirth ?? '';
    if (trimmedDob !== initialDob) {
      next.date_of_birth = trimmedDob === '' ? null : trimmedDob;
    }
    if (deceased !== (member.deceased === true)) {
      next.deceased = deceased;
    }
    const initialNotes = member.notes ?? '';
    if (trimmedNotes !== initialNotes) {
      next.notes = trimmedNotes.length === 0 ? null : trimmedNotes;
    }
    return next;
  }, [contactStatus, sex, initialSex, dateOfBirth, deceased, notes, member]);

  const hasChanges = Object.keys(body).length > 0;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent className="max-w-md" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.family.members.editDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.family.members.editDialog.description', { name })}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!hasChanges || busy) return;
            onSubmit(body);
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="edit-member-contact-status">
              {t('routes.family.members.table.contactStatus')}
            </Label>
            <Select
              id="edit-member-contact-status"
              value={contactStatus}
              onChange={(e) => {
                setContactStatus(e.target.value as CascadeContactStatus);
              }}
            >
              {CONTACT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`family.contactStatus.${s}`)}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="edit-member-sex">
                {t('routes.family.members.editDialog.sex')}
              </Label>
              <Select
                id="edit-member-sex"
                value={sex}
                onChange={(e) => {
                  const next = e.target.value;
                  setSex(next === '' ? '' : isSexCode(next) ? next : '');
                }}
              >
                <option value="">{t('routes.family.members.editDialog.sexUnspecified')}</option>
                {SEX_CODES.map((code) => (
                  <option key={code} value={code}>
                    {t(`patient.sex.${code}`)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-member-dob">
                {t('routes.family.members.editDialog.dateOfBirth')}
              </Label>
              <Input
                id="edit-member-dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => {
                  setDateOfBirth(e.target.value);
                }}
              />
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={deceased}
              onChange={(e) => {
                setDeceased(e.target.checked);
              }}
              className="h-3.5 w-3.5"
            />
            {t('routes.family.members.editDialog.deceased')}
          </label>

          <div className="space-y-1">
            <Label htmlFor="edit-member-notes">
              {t('routes.family.members.editDialog.notes')}
            </Label>
            <Textarea
              id="edit-member-notes"
              value={notes}
              rows={3}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              placeholder={t('routes.family.members.editDialog.notesPlaceholder')}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="submit"
              disabled={!hasChanges || busy}
              loading={busy}
              loadingText={t('patient.dirtyFooter.saving')}
            >
              {t('routes.family.members.editDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
